import { graphql, useStaticQuery } from "gatsby";
import React, { useEffect, useState } from "react";
import { use } from "react";

import NewProgramCard from "../../../components/cards/newProgramCard";
import { useBreakpoint } from "../../../hooks/useBreakpoint";
import {
  preFilterGroupPrograms,
  preFilterStandardPrograms
} from "../../../utils/base";
import { uniqBy } from "../../../utils/commonFunctions";
import ProgramApplyCard from "../../cards/programApplyCard";
import { ConditionalSwiper } from "../conditionalSwiper";
import { getNewProgramCardDetails } from "../flex/newProgramCardObject";
import { getProgramIngoDetails } from "../flex/programApplyCardIngoObject";

const LongFocusLocationPrograms = ({
  focusList,
  groupProgramSourceType,
  getFilterByProgramType,
  getFilterByProgramExtraTypes,
  getFilterByProgramFocus,
  getFilterByLocation,
  programListFilterStrict,
  locationTitle,
  programListSectionTitle,
  setFilterByProgramType,
  setFilterByProgramLocation
}) => {
  const breakpoint = useBreakpoint();
  const { programs, groupPrograms, featuredIncludes, genericDetails } =
    useStaticQuery(graphql`
      {
        featuredIncludes: wp {
          acfOptionsWhatsIncluded {
            whatsIncluded {
              featuredWhatsIncluded {
                featuredWhatsinludedTitle {
                  title
                }
                featuredWhatsincludedProductType {
                  ... on WpProgramType {
                    slug
                  }
                }
              }
            }
          }
        }

        programs: allWpProgram(
          # filter: { acfProgramsMeta: { programIngo: { ne: true } } }
          # filter: { acfProgramsMeta: { programJourneys: { ne: true } } }
          sort: { acfProgramsMeta: { programRating: DESC } }
        ) {
          nodes {
            ...programMiniDataFilterableFragment
          }
        }
        groupPrograms: allWpGroupProgram {
          nodes {
            ...groupProgramMiniDataFilterableFragment
          }
        }
        genericDetails: wp {
          ingoPrograms: acfOptionsIngoProgramsConfig {
            ingoPrograms {
              ingoRunAndLedByFlex
              ingoRunAndLedByGvi
            }
          }
        }
      }
    `);

  const runAndLedByGVi =
    genericDetails?.ingoPrograms?.ingoPrograms?.ingoRunAndLedByGvi;
  const runAndLedByFlex =
    genericDetails?.ingoPrograms?.ingoPrograms?.ingoRunAndLedByFlex;

  const defaultStandardPrograms =
    programs?.nodes?.filter((program) =>
      preFilterStandardPrograms(
        program,
        getFilterByProgramType,
        getFilterByProgramExtraTypes,
        getFilterByLocation,
        getFilterByProgramFocus,
        programListFilterStrict
      )
    ) ?? [];
  const defaultGroupPrograms =
    groupPrograms?.nodes
      ?.filter((grpProgram) =>
        preFilterGroupPrograms(
          grpProgram,
          getFilterByProgramType,
          getFilterByProgramExtraTypes,
          getFilterByLocation,
          getFilterByProgramFocus,
          programListFilterStrict
        )
      )
      ?.map((grpProgram) => ({
        ...grpProgram,
        uri: grpProgram?.uri?.replace(
          "group-travel-single",
          "group-travel/schools"
        )
      })) ?? [];
  const defaultPrograms = groupProgramSourceType
    ? defaultGroupPrograms
    : defaultStandardPrograms;

  const restructureProgram = {};
  const geographicalList = defaultPrograms?.flatMap(
    (program) => program?.geographical?.nodes
  );
  geographicalList?.forEach((location) => {
    restructureProgram[location.slug] = { ...location, programs: [] };
    defaultPrograms?.forEach((program) => {
      if (
        program?.geographical?.nodes
          ?.flatMap((loc) => loc.slug)
          ?.includes(location.slug)
      ) {
        restructureProgram[location.slug].programs.push(program);
      }
    });
  });

  let tabHeaders = [];
  let idx = 1;
  // restructureProgram.sort();
  // eslint-disable-next-line no-unused-vars
  for (const [key, detail] of Object.entries(restructureProgram).sort()) {
    if (detail.parentId === null) {
      let subheaders = [];
      let cidx = 1;
      // eslint-disable-next-line no-unused-vars
      for (const [ckey, cdetail] of Object.entries(restructureProgram).sort()) {
        if (cdetail.parentId !== null && detail.id === cdetail.parentId) {
          subheaders.push({
            id: cidx,
            text: cdetail.name,
            programs: cdetail.programs
          });
          cidx++;
        }
      }
      tabHeaders.push({
        id: idx,
        title: detail.name,
        programs: detail.programs,
        subHeaders: subheaders
      });
      idx++;
    }
  }

  const defaultProgramTypeList = defaultPrograms
    ?.flatMap((program) => program?.acfProgramsMeta?.programTypes)
    ?.flatMap((type) => type?.slug);

  let groupByTypeList = [];

  if (defaultProgramTypeList?.length > 0) {
    defaultProgramTypeList.forEach((type) => {
      groupByTypeList[type] = (groupByTypeList[type] || 0) + 1;
    });
  }

  const featuredIncludesList =
    featuredIncludes?.acfOptionsWhatsIncluded?.whatsIncluded?.featuredWhatsIncluded
      ?.flatMap((itm) => itm)
      ?.map((item) => ({
        types: item?.featuredWhatsincludedProductType?.map(
          (lItem) => lItem?.slug
        ),
        features: item?.featuredWhatsinludedTitle?.map((featItem) => ({
          name: featItem?.title
        }))
      }));

  useEffect(() => {
    setFilterByProgramType(groupByTypeList);
  }, []);

  useEffect(() => {
    const duplicateGeographicals = Array.from(geographicalList);

    const getContinent = duplicateGeographicals
      .filter((item) => item.parentId === null)
      .flatMap((item) => item.id);

    const reformattedGeolist = duplicateGeographicals?.filter((location) => {
      return getContinent.includes(location.parentId);
    });

    const reformattedGeoList = uniqBy(reformattedGeolist, "slug").map(
      (item) => item.slug
    );

    const restructureProgramLocation = {};
    const groupByLocationList = [];

    if (reformattedGeoList && reformattedGeoList?.length > 0) {
      (Array.isArray(reformattedGeoList)
        ? reformattedGeoList
        : [reformattedGeoList]
      )?.forEach((location) => {
        restructureProgramLocation[location] = { location, programs: [] };

        defaultPrograms?.forEach((program) => {
          if (
            program?.geographical?.nodes
              ?.flatMap((loc) => loc.slug)
              ?.includes(location)
          ) {
            restructureProgramLocation[location].programs.push(program);
            groupByLocationList[location] =
              restructureProgramLocation[location].programs.length;
          }
        });
      });
    }

    setFilterByProgramLocation(groupByLocationList);
  }, []);

  if (defaultPrograms?.length < 1) {
    return <></>;
  }

  return (
    <>
      <section className="flex flex-col items-center justify-center w-screen md:w-full">
        <div className="container flex flex-col w-full">
          <div className={"px-4 sm:px-0"}>
            <div className="flex flex-row justify-center summary-section">
              <div className="w-full mx-auto">
                <div className={`relative w-full`}>
                  <div className="relative flex sm:flex-col w-full py-0">
                    <ConditionalSwiper
                      condition={breakpoint === "xs"}
                      slidesPerView={1.3}
                      spaceBetween={20}
                      centeredSlides={false}
                      className="h-full w-full"
                    >
                      {defaultPrograms &&
                        defaultPrograms.length > 0 &&
                        defaultPrograms.map((program, idx) => {
                          //reassign the property value
                          program.id = program.databaseId;

                          const programIngo =
                            program?.programIngo ??
                            program?.acfProgramsMeta?.programIngo ??
                            false;

                          const programTypes =
                            program?.acfProgramsMeta?.programTypes?.flatMap(
                              (type) => type?.slug
                            ) || [];

                          const programJourney = programTypes.some((type) =>
                            /journey/gi.test(type)
                          );

                          const mobileCardDetails = getProgramIngoDetails({
                            programIngo: false,
                            journeyProgram: programJourney,
                            additionalCardClass: programIngo
                              ? "sm:h-64 sm:max-h-64"
                              : "sm:h-80 sm:max-h-80 2xl:h-96 2xl:max-h-96",
                            program,
                            featuredIncludesList,
                            extraOptions: {
                              cardClickable: false,
                              isProgramFocusPage: true,
                              target: "application",
                              type: "apply",
                              rightContainerSectionClass:
                                "gap-2 pt-3 sm:pt-0 sm:gap-5",
                              containerClass:
                                "relative w-full mb-6 md:mb-10 rounded-md overflow-hidden border-2 hover:border-3 sm:h-full flex",
                              mainProgramClass:
                                "text-mob md:text-base leading-genericCta font-bold md:font-normal text-left text-black-lighter dark:text-white xxs:pt-2  sm:min-h-0 sm:h-auto",
                              travelInnerContainerClass:
                                " sm:gap-2 sm:px-1 sm:py-3 inline-block bg-black-lighter dark:bg-transparent",
                              ctaViewExtraClass:
                                " xxs:bg-transparent xxs:hover:bg-white xxs:text-white xxs:border-white ",
                              ctaViewArrowClass:
                                " xxs:stroke-white xxs:group-hover:stroke-black-light",
                              labelClass:
                                "text-base leading-genericCta font-bold pr-2 text-white dark:text-black-light group-hover:text-black-light group-hover:dark:text-green-standard"
                            }
                          });

                          const details = getNewProgramCardDetails({
                            programIngo: false,
                            journeyProgram: programJourney,
                            program,
                            featuredIncludesList,
                            url: program?.uri,
                            extraOptions: {
                              cardClickable: true
                            }
                          });

                          return breakpoint === "xs" ? (
                            <ProgramApplyCard
                              key={`program-card-key-mobile-${idx}`}
                              {...mobileCardDetails}
                              runAndLedByGVi={runAndLedByGVi}
                              runAndLedByFlex={runAndLedByFlex}
                              showProgramRating={false}
                            />
                          ) : (
                            <NewProgramCard
                              key={`program-card-key-desktop${idx}`}
                              descWidth="w-full"
                              openShareWithStatus={false}
                              {...details}
                              runAndLedByGVi={runAndLedByGVi}
                              runAndLedByFlex={runAndLedByFlex}
                            />
                          );
                        })}
                    </ConditionalSwiper>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};
export default LongFocusLocationPrograms;
